import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gauge, CheckCircle2, AlertTriangle, RefreshCw, ChevronRight, Table2, ShieldCheck, Plus, Download, Mail, Upload } from "lucide-react";

// Pricing Readiness — one honest view of what still blocks a FIRM price (wage-table load state, un/priced &
// unmatched CLINs, open estimate items) with one-click jumps. Re-evaluate re-grounds the WD rate table and
// re-reconciles the government CLIN schedule WITHOUT a full engine run.
function jumpTo(sel) {
  const el = document.querySelector(sel);
  if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList?.add("ring-2", "ring-gold"); setTimeout(() => el.classList?.remove("ring-2", "ring-gold"), 1600); }
}

export function PricingReadinessPanel({ dealId, isOfficer, reload }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regBusy, setRegBusy] = useState(false);
  const [bondOpen, setBondOpen] = useState(false);
  const [bondBusy, setBondBusy] = useState(false);
  const [bf, setBf] = useState({ surety_name: "", bid_guarantee_amount: "", bid_guarantee_pct: "", pp_bond_rate: "", quote_date: "" });

  const load = useCallback(async () => {
    try { const res = await api.get(`/bids/${dealId}/pricing/readiness`); setR(res.data); }
    catch { /* non-blocking */ }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const reevaluate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/bids/${dealId}/pricing/reevaluate`);
      setR(res.data);
      toast.success("Pricing readiness re-evaluated");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-evaluate failed"); }
    finally { setBusy(false); }
  };

  // Dir 146 — RE-GROUND IN PLACE: re-run line-level comp grounding against the current cost model without a
  // full engine run, so a stale UNGROUNDED line (e.g. after a new cited band shipped) refreshes right here.
  const reground = async () => {
    setRegBusy(true);
    try {
      const res = await api.post(`/bids/${dealId}/pricing/reground`);
      setR(res.data);
      const g = res.data?.line_grounding;
      toast.success(g && g.ungrounded === 0
        ? `Re-grounded — all ${g.total} cost line${g.total > 1 ? "s" : ""} grounded`
        : `Re-grounded — ${g?.ungrounded || 0} line${(g?.ungrounded || 0) > 1 ? "s" : ""} still need grounding`);
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-ground failed"); }
    finally { setRegBusy(false); }
  };

  const saveBond = async () => {
    if (!bf.surety_name.trim()) { toast.error("Surety name is required"); return; }
    if (!bf.bid_guarantee_amount && !bf.bid_guarantee_pct) { toast.error("Enter a bid-guarantee amount or percentage"); return; }
    setBondBusy(true);
    try {
      const res = await api.post(`/bids/${dealId}/bond-quote`, bf);
      if (res.data?.readiness) setR(res.data.readiness);
      toast.success("Surety quote recorded — pre-filled SF-24 ready to download");
      setBondOpen(false);
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record bond quote"); }
    finally { setBondBusy(false); }
  };

  const attestBond = async () => {
    setBondBusy(true);
    try {
      const res = await api.post(`/bids/${dealId}/bid-bond/attest`, { note: "Executed bid bond furnished with the bid at submission." });
      if (res.data?.readiness) setR(res.data.readiness);
      toast.success("Bid-bond attestation logged — deliverable satisfied");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log attestation"); }
    finally { setBondBusy(false); }
  };

  // Dir 120 — prepare the ONE Swiftbonds bid-bond-request package (order form first + contract docs/pricing).
  const [sbOpen, setSbOpen] = useState(false);
  const [sbBusy, setSbBusy] = useState(false);
  const [sbResult, setSbResult] = useState(null);
  const [sb, setSb] = useState({ current_work_on_hand: "", work_on_hand_as_of: "", percent_subcontracted: "", major_subcontract_items: "" });
  const [dispatchBusy, setDispatchBusy] = useState(false);
  const [execBusy, setExecBusy] = useState(false);
  const execRef = useRef(null);
  const requestSwiftbonds = async () => {
    setSbBusy(true);
    try {
      const res = await api.post(`/bids/${dealId}/bid-bond/request-swiftbonds`, { backlog: sb });
      setSbResult(res.data);
      const miss = (res.data?.missing || []).length;
      toast.success(`Bid-bond request prepared for Gary @ Swiftbonds${miss ? ` — ${miss} WD field(s) still needed` : ""}. Operator sends.`);
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare the bond request"); }
    finally { setSbBusy(false); }
  };

  // Automated bid-bond request (GOVERNMENT bids only) — fills the Swiftbonds order form, attaches the price
  // summary + solicitation docs, and DISPATCHES it to the surety. Dispatch clears the SF-24 send-gate blocker.
  const dispatchRequest = async () => {
    setDispatchBusy(true);
    try {
      const res = await api.post(`/bids/${dealId}/bid-bond/dispatch-request`, { backlog: sb });
      if (res.data?.readiness) setR(res.data.readiness);
      const miss = (res.data?.missing || []).length;
      toast.success(`Bid bond request dispatched to Swiftbonds${miss ? ` — ${miss} WD field(s) still flagged` : ""}. Send-gate blocker cleared.`);
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not dispatch the bond request"); }
    finally { setDispatchBusy(false); }
  };

  // One-click executed-bond attachment — after dispatch, ingest the surety-returned signed SF-24. Flips the
  // bond row Requested → in-package (the stronger deliverable state) and completes the audit chain.
  const attachExecuted = async (file, confirm = false) => {
    if (!file) return;
    setExecBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (confirm) fd.append("confirm", "true");
      const res = await api.post(`/bids/${dealId}/bid-bond/executed`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.requires_confirmation) {
        if (window.confirm(`${res.data.warning}\n\nAttach it as the executed bid bond anyway?`)) {
          return await attachExecuted(file, true);
        }
        return;
      }
      if (res.data?.readiness) setR(res.data.readiness);
      toast.success("Executed SF-24 attached & merged — bid-bond chain complete.");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not attach the executed bond"); }
    finally { setExecBusy(false); if (execRef.current) execRef.current.value = ""; }
  };

  // Surety-side reply capture — the officer pastes the quote #/premium and/or drops the surety's .eml email
  // reply (or the bond PDF). Backend auto-extracts & merges the executed SF-24 and firms the bond premium.
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBusy, setReplyBusy] = useState(false);
  const replyFileRef = useRef(null);
  const [rf, setRf] = useState({ quote_number: "", premium: "", surety_name: "", pp_bond_rate: "", notes: "", file: null, fileName: "" });
  const onReplyFile = (e) => { const f = e.target.files?.[0]; setRf((s) => ({ ...s, file: f || null, fileName: f?.name || "" })); };
  const submitSuretyReply = async () => {
    if (!rf.quote_number && !rf.premium && !rf.pp_bond_rate && !rf.file) {
      toast.error("Paste the quote/premium or attach the surety's .eml email / bond PDF."); return;
    }
    setReplyBusy(true);
    try {
      const fd = new FormData();
      if (rf.quote_number) fd.append("quote_number", rf.quote_number);
      if (rf.premium) fd.append("premium", rf.premium);
      if (rf.surety_name) fd.append("surety_name", rf.surety_name);
      if (rf.pp_bond_rate) fd.append("pp_bond_rate", rf.pp_bond_rate);
      if (rf.notes) fd.append("notes", rf.notes);
      if (rf.file) fd.append("reply", rf.file);
      const res = await api.post(`/bids/${dealId}/bid-bond/surety-reply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.readiness) setR(res.data.readiness);
      const bits = [];
      if (res.data?.merged) bits.push("executed SF-24 attached → in-package");
      if (res.data?.premium_firmed) bits.push("bond premium firmed");
      toast.success("Surety reply captured" + (bits.length ? ` — ${bits.join(", ")}.` : "."));
      setReplyOpen(false);
      setRf({ quote_number: "", premium: "", surety_name: "", pp_bond_rate: "", notes: "", file: null, fileName: "" });
      if (replyFileRef.current) replyFileRef.current.value = "";
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not capture the surety reply"); }
    finally { setReplyBusy(false); }
  };

  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  if (!r || (!r.wage_table?.applicable && !(r.open_items || []).length && !r.clins?.gov_count && !r.bond?.required && !r.market_comps && !(r.coverage?.clins))) return null;

  const firm = r.firm;
  const wt = r.wage_table || {};
  const cl = r.clins || {};
  const bond = r.bond || {};
  const open = r.open_items || [];
  const unmatched = cl.unmatched || [];
  const mc = r.market_comps || null;
  const cov = r.coverage || null;
  const lg = r.line_grounding || null;

  return (
    <div className="mt-5 bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="pricing-readiness-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-gold-dark" />
          <h3 className="font-display font-semibold text-sm text-zinc-900">Pricing Readiness</h3>
          <span
            data-testid="pricing-readiness-state"
            className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${firm ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
            {firm ? "FIRM" : "PRICE PENDING"}
          </span>
          {cov && cov.clins > 0 && (
            <span data-testid="pricing-coverage-chip" title="Per-CLIN pricing coverage (internal — not shipped in the package)"
              className="text-[11px] px-2 py-0.5 rounded-full border font-semibold bg-zinc-100 text-zinc-700 border-zinc-300">
              {cov.firm} FIRM{cov.pending ? ` · ${cov.pending} PENDING` : ""}{cov.gap ? ` · ${cov.gap} GAP` : ""}
            </span>
          )}
          {lg && lg.total > 0 && (
            <span data-testid="pricing-firmness-chip"
              title="Cost lines grounded against a cited comparable band (Dir 146)."
              className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${lg.ungrounded === 0 ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-red-100 text-red-800 border-red-300"}`}>
              {lg.grounded}/{lg.total} lines grounded{lg.ungrounded ? ` · ${lg.ungrounded} ungrounded` : ""}
            </span>
          )}
          {lg && lg.total > 0 && (
            <button onClick={reground} disabled={regBusy} data-testid="pricing-reground-chip-btn"
              title="Re-run line grounding in place (no engine run) — refresh grounding after a new cited band ships"
              className="p-1 rounded-full border border-zinc-300 text-zinc-600 hover:bg-zinc-100 disabled:opacity-50">
              <RefreshCw className={`h-3 w-3 ${regBusy ? "animate-spin" : ""}`} />
            </button>
          )}
          {bond.premium_has_line && (
            <span data-testid="bond-premium-chip"
              title="Bond premium cost line — FIRM once a recorded surety rate is wired in; ESTIMATE while it is a placeholder (Dir 79)."
              className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${bond.premium_firm ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
              {bond.premium_firm
                ? `Bond premium: FIRM${bond.premium_amount != null ? ` $${Number(bond.premium_amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""} (surety rate)`
                : "Bond premium: ESTIMATE"}
            </span>
          )}
        </div>
        {isOfficer && (
          <button onClick={reevaluate} disabled={busy} data-testid="pricing-reevaluate-btn"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Re-evaluate
          </button>
        )}
      </div>

      {cov && cov.clins > 0 && (
        <div className="mb-3 border border-zinc-100 rounded-lg p-3" data-testid="pricing-coverage-rollup">
          <div className="text-[11px] font-semibold text-zinc-800 mb-1.5">Pricing Coverage (per CLIN) — internal, not shipped in the package</div>
          <div className="flex flex-wrap gap-1.5">
            {cov.rows.map((c, i) => (
              <span key={i} data-testid={`coverage-clin-${i}`}
                className={`text-[10px] px-2 py-0.5 rounded-md border ${
                  c.status === "FIRM" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : c.status === "PENDING" ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-zinc-100 text-zinc-600 border-zinc-300"}`}>
                {c.clin}: {c.status}{c.amount != null ? ` ($${Number(c.amount).toLocaleString(undefined, {maximumFractionDigits: 0})})` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {lg && lg.ungrounded > 0 && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="pricing-firmness-ungrounded">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
            <span className="text-[11px] font-semibold text-red-800">
              {lg.ungrounded} of {lg.total} cost line{lg.total > 1 ? "s" : ""} not yet grounded against a cited comparable band
            </span>
          </div>
          <ul className="text-[11px] text-red-800 space-y-0.5 max-h-28 overflow-auto" data-testid="pricing-firmness-ungrounded-list">
            {(lg.ungrounded_lines || []).map((ln, i) => (
              <li key={i} data-testid={`pricing-ungrounded-line-${i}`}>• {ln}</li>
            ))}
          </ul>
          <div className="mt-1.5 flex items-center gap-3 flex-wrap">
            <button onClick={reground} disabled={regBusy} data-testid="pricing-reground-btn"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:underline disabled:opacity-50">
              <RefreshCw className={`h-3 w-3 ${regBusy ? "animate-spin" : ""}`} /> {regBusy ? "Re-grounding…" : "Re-ground now (no engine run)"}
            </button>
            <button onClick={() => jumpTo('[data-testid="pricing-grounding-card"]')} data-testid="pricing-jump-grounding"
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
              Ground these lines (supply a comp or operator estimate) <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Wage table */}
        {wt.applicable && (
          <div className="border border-zinc-100 rounded-lg p-3" data-testid="pricing-readiness-wage">
            <div className="flex items-center gap-2 mb-1">
              {wt.loaded ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
              <span className="text-xs font-semibold text-zinc-800">Wage determination</span>
            </div>
            <p className="text-[11px] text-zinc-600">
              {wt.loaded ? (
                <>Rate table loaded — <span className="font-medium">{wt.wd_number}{wt.revision != null ? ` r${wt.revision}` : ""}</span>, <span className="font-medium">{wt.occupations_loaded}</span> classification rates.</>
              ) : (
                <>{(wt.regime || "prevailing-wage")} determination <span className="font-medium">{wt.status === "referenced_not_loaded" ? "located but rate table NOT loaded" : "not loaded"}</span> — labor not grounded.</>
              )}
            </p>
            {!wt.loaded && (
              <button onClick={() => jumpTo('[data-testid="wd-ground-chip"]')} data-testid="pricing-jump-wage"
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
                Ground the wage determination <ChevronRight className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* CLIN schedule */}
        <div className="border border-zinc-100 rounded-lg p-3" data-testid="pricing-readiness-clins">
          <div className="flex items-center gap-2 mb-1">
            {unmatched.length === 0 && (cl.gov_count || 0) > 0 ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Table2 className="h-3.5 w-3.5 text-zinc-500" />}
            <span className="text-xs font-semibold text-zinc-800">Government CLIN schedule</span>
          </div>
          <p className="text-[11px] text-zinc-600">
            {(cl.gov_count || 0) > 0 ? (
              <><span className="font-medium">{cl.priced_count || 0}</span> of <span className="font-medium">{cl.gov_count}</span> CLIN(s) priced{unmatched.length ? "," : "."}
                {unmatched.length ? <> <span className="font-medium text-amber-700">{unmatched.length} unmatched</span>: {unmatched.join(", ")}.</> : null}</>
            ) : (<>No government CLIN schedule reconciled yet.</>)}
          </p>
          {(cl.math_errors || []).length > 0 && (
            <p className="text-[11px] text-red-600 mt-1">{cl.math_errors.length} schedule math error(s).</p>
          )}
          {(unmatched.length > 0 || (cl.gov_count || 0) === 0) && (
            <button onClick={() => jumpTo('[data-testid="documents-card"]')} data-testid="pricing-jump-clins"
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
              Review the solicitation schedule <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Surety bond (bid guarantee) */}
        {bond.required && (
          <div className="border border-zinc-100 rounded-lg p-3 sm:col-span-2" data-testid="pricing-readiness-bond">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                {bond.satisfied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <ShieldCheck className="h-3.5 w-3.5 text-amber-600" />}
                <span className="text-xs font-semibold text-zinc-800">Bid guarantee (surety bond)</span>
              </div>
              {isOfficer && !bondOpen && (
                <button onClick={() => setBondOpen(true)} data-testid="bond-add-btn"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline">
                  <Plus className="h-3 w-3" /> {bond.recorded ? "Update quote" : "Record surety quote"}
                </button>
              )}
            </div>
            {bond.government && (
              <div className="mb-2 rounded-md border border-indigo-200 bg-indigo-50/60 p-2" data-testid="bond-dispatch-block">
                {bond.request_dispatched ? (
                  <div data-testid="bond-dispatch-done">
                    <p className="text-[11px] text-emerald-800 font-medium">
                      Bid bond request DISPATCHED to {bond.request_dispatch?.to || "Swiftbonds"}
                      {bond.request_dispatch?.at ? ` on ${new Date(bond.request_dispatch.at).toLocaleDateString()}` : ""} — send-gate bid-bond blocker cleared.
                      {(bond.request_dispatch?.missing || []).length > 0 && (
                        <span className="block text-amber-700 mt-0.5">Still needed from WD: {(bond.request_dispatch.missing).join("; ")}</span>
                      )}
                    </p>
                    {bond.in_package ? (
                      <p className="mt-1.5 text-[11px] font-semibold text-emerald-700 flex items-center gap-1" data-testid="bond-chain-complete">
                        <CheckCircle2 className="h-3 w-3" /> Executed bond received &amp; in the package — chain complete (requested → issued).
                      </p>
                    ) : isOfficer ? (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid="bond-attach-executed">
                        <span className="text-[11px] text-indigo-900">When Swiftbonds returns the signed SF-24, attach it here to complete the chain:</span>
                        <input ref={execRef} type="file" accept="application/pdf" className="hidden"
                          onChange={(e) => attachExecuted(e.target.files?.[0])} data-testid="bond-executed-input" />
                        <button onClick={() => execRef.current?.click()} disabled={execBusy} data-testid="bond-attach-executed-btn"
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-emerald-700 text-white px-3 py-1.5 rounded-md hover:bg-emerald-800 disabled:opacity-50">
                          {execBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                          {execBusy ? "Attaching…" : "Attach signed SF-24 from Swiftbonds"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-indigo-900">Automated bid-bond request — during testing this routes to <span className="font-semibold">ironclad@williamsdiversified.com</span> (NOT the surety) for verification.</span>
                    {isOfficer && (
                      <button onClick={dispatchRequest} disabled={dispatchBusy} data-testid="bond-dispatch-btn"
                        title="Pre-deployment: routes to ironclad@williamsdiversified.com for verification — Gary/Swiftbonds is not emailed until deployment."
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-indigo-700 text-white px-3 py-1.5 rounded-md hover:bg-indigo-800 disabled:opacity-50">
                        {dispatchBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                        {dispatchBusy ? "Dispatching…" : "Send bid bond request (test → ironclad@)"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {!bond.in_package && (
              <div className="mb-2 rounded-md border border-zinc-200 bg-muted/40 p-2" data-testid="surety-reply-block">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-zinc-700 flex items-center gap-1">
                    <Mail className="h-3 w-3 text-zinc-500" /> Got a reply from the surety? Capture the quote/premium or drop the email — Anvil auto-extracts the executed SF-24 and firms the premium.
                  </span>
                  {isOfficer && (
                    <button onClick={() => setReplyOpen((v) => !v)} data-testid="surety-reply-toggle-btn"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold border border-zinc-300 text-zinc-800 px-2.5 py-1 rounded-md hover:bg-white">
                      <Mail className="h-3 w-3" /> {replyOpen ? "Hide" : "Capture surety reply"}
                    </button>
                  )}
                </div>
                {replyOpen && isOfficer && (
                  <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="surety-reply-form">
                    <input value={rf.quote_number} onChange={(e) => setRf({ ...rf, quote_number: e.target.value })} placeholder="Quote / bond number"
                      className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="surety-reply-quote" />
                    <input value={rf.premium} onChange={(e) => setRf({ ...rf, premium: e.target.value })} placeholder="Premium ($)"
                      className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="surety-reply-premium" />
                    <input value={rf.surety_name} onChange={(e) => setRf({ ...rf, surety_name: e.target.value })} placeholder="Surety name (optional)"
                      className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="surety-reply-surety" />
                    <input value={rf.pp_bond_rate} onChange={(e) => setRf({ ...rf, pp_bond_rate: e.target.value })} placeholder="P&P bond rate % (optional)"
                      className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="surety-reply-pprate" />
                    <textarea value={rf.notes} onChange={(e) => setRf({ ...rf, notes: e.target.value })} placeholder="Notes (optional)" rows={2}
                      className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5 sm:col-span-2" data-testid="surety-reply-notes" />
                    <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                      <label data-testid="surety-reply-file-label"
                        className="inline-flex items-center gap-1 text-[11px] border border-zinc-300 text-zinc-700 px-2.5 py-1.5 rounded-md cursor-pointer hover:bg-white">
                        <Upload className="h-3 w-3" /> {rf.fileName ? rf.fileName : "Attach .eml / bond PDF (optional)"}
                        <input ref={replyFileRef} type="file" accept=".eml,message/rfc822,application/pdf" className="hidden"
                          onChange={onReplyFile} data-testid="surety-reply-file" />
                      </label>
                      <button onClick={submitSuretyReply} disabled={replyBusy} data-testid="surety-reply-submit-btn"
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-indigo-700 text-white px-3 py-1.5 rounded-md hover:bg-indigo-800 disabled:opacity-50">
                        {replyBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} {replyBusy ? "Capturing…" : "Capture reply"}
                      </button>
                      <button onClick={() => setReplyOpen(false)} data-testid="surety-reply-cancel-btn"
                        className="text-[11px] text-zinc-500 hover:text-zinc-800">Cancel</button>
                      <span className="text-[10px] text-zinc-400">A bare quote/premium firms the premium; only a real executed SF-24 completes the in-package chain.</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {bond.recorded ? (
              <>
                <p className="text-[11px] text-zinc-600" data-testid="bond-recorded-line">
                  Quote recorded — <span className="font-medium">{bond.surety_name}</span>
                  {bond.bid_guarantee_amount != null ? `, $${Number(bond.bid_guarantee_amount).toLocaleString()}` : (bond.bid_guarantee_pct != null ? `, ${bond.bid_guarantee_pct}% of bid` : "")}
                  {bond.quote_date ? `, quoted ${bond.quote_date}` : ""}.
                </p>
                <p className={`text-[11px] mt-0.5 ${bond.in_package ? "text-emerald-700" : "text-amber-700"}`} data-testid="bond-deliverable-line">
                  {bond.in_package
                    ? (bond.attested ? "Executed bid bond attested at submission — deliverable satisfied." : "Executed SF-24 merged — deliverable satisfied.")
                    : "Planning only — the EXECUTED SF-24 is not yet in the package. Execute the pre-filled SF-24 with the surety and merge it, or log the at-submission attestation."}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <a href={`${API_BASE}/bids/${dealId}/sf24-prefill.pdf`} download data-testid="sf24-download"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold bg-zinc-900 text-white px-2.5 py-1.5 rounded-md hover:bg-zinc-800">
                    <Download className="h-3 w-3" /> Download pre-filled SF-24
                  </a>
                  {isOfficer && !bond.in_package && (
                    <button onClick={attestBond} disabled={bondBusy} data-testid="bond-attest-btn"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold border border-zinc-300 text-zinc-800 px-2.5 py-1.5 rounded-md hover:bg-zinc-50 disabled:opacity-50">
                      {bondBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Attest bond at submission
                    </button>
                  )}
                  <span className="text-[10px] text-zinc-400">Quote is platform-only — never in the CO package.</span>
                </div>
                {/* Dir 120 — Swiftbonds ONE-package bid-bond request (order form first + contract docs/pricing) */}
                {isOfficer && (
                  <div className="mt-2 border-t border-zinc-100 pt-2" data-testid="swiftbonds-request">
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={() => setSbOpen(!sbOpen)} data-testid="swiftbonds-toggle-btn"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold border border-indigo-300 text-indigo-800 px-2.5 py-1.5 rounded-md hover:bg-indigo-50">
                        <ShieldCheck className="h-3 w-3" /> Prepare Swiftbonds bid-bond request
                      </button>
                      <span className="text-[10px] text-zinc-400">→ Gary Eastman (gary@swiftbonds.com), cc govcon. Operator sends.</span>
                    </div>
                    {sbOpen && (
                      <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="swiftbonds-backlog-form">
                        <input value={sb.current_work_on_hand} onChange={(e) => setSb({ ...sb, current_work_on_hand: e.target.value })} placeholder="Current work on hand — amount ($)"
                          className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="sb-woh-amount" />
                        <input value={sb.work_on_hand_as_of} onChange={(e) => setSb({ ...sb, work_on_hand_as_of: e.target.value })} type="date"
                          className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="sb-woh-asof" />
                        <input value={sb.percent_subcontracted} onChange={(e) => setSb({ ...sb, percent_subcontracted: e.target.value })} placeholder="Percent to be subcontracted (%)"
                          className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="sb-pct-subbed" />
                        <input value={sb.major_subcontract_items} onChange={(e) => setSb({ ...sb, major_subcontract_items: e.target.value })} placeholder="Major subcontract items & estimates"
                          className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="sb-major-subs" />
                        <div className="sm:col-span-2 flex items-center gap-2">
                          <button onClick={requestSwiftbonds} disabled={sbBusy} data-testid="swiftbonds-request-btn"
                            className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-indigo-700 text-white px-3 py-1.5 rounded-md hover:bg-indigo-800 disabled:opacity-50">
                            {sbBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} Prepare ONE package
                          </button>
                          <span className="text-[10px] text-zinc-400">Blank fields are left empty (never fabricated) and flagged for follow-up.</span>
                        </div>
                      </div>
                    )}
                    {sbResult && (
                      <div className="mt-2 text-[11px]" data-testid="swiftbonds-result">
                        <a href={`${API_BASE}/bids/${dealId}/forms/file/bond-request-swiftbonds`} target="_blank" rel="noreferrer" data-testid="swiftbonds-package-download"
                          className="inline-flex items-center gap-1 font-semibold bg-zinc-900 text-white px-2.5 py-1.5 rounded-md hover:bg-zinc-800">
                          <Download className="h-3 w-3" /> Download the ONE package
                        </a>
                        <span className="ml-2 text-zinc-600">Prepared for {sbResult?.email?.to}, cc {(sbResult?.email?.cc || []).join(", ")}.</span>
                        {(sbResult?.missing || []).length > 0 && (
                          <p className="mt-1 text-amber-700" data-testid="swiftbonds-missing">Still needed from WD: {(sbResult.missing).join("; ")}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-[11px] text-zinc-600">A bid guarantee is required (FAR 52.228-1). Record the surety quote to auto-prep the SF-24; the EXECUTED bond must be in the package (or attested) before sending.</p>
            )}
            {bondOpen && (
              <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="bond-quote-form">
                <input value={bf.surety_name} onChange={(e) => setBf({ ...bf, surety_name: e.target.value })} placeholder="Surety name (e.g. Swiftbonds)"
                  className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="bond-surety-input" />
                <input value={bf.quote_date} onChange={(e) => setBf({ ...bf, quote_date: e.target.value })} type="date"
                  className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="bond-date-input" />
                <input value={bf.bid_guarantee_amount} onChange={(e) => setBf({ ...bf, bid_guarantee_amount: e.target.value })} placeholder="Bid-guarantee amount ($)"
                  className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="bond-amount-input" />
                <input value={bf.bid_guarantee_pct} onChange={(e) => setBf({ ...bf, bid_guarantee_pct: e.target.value })} placeholder="or % of bid (e.g. 20)"
                  className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5" data-testid="bond-pct-input" />
                <input value={bf.pp_bond_rate} onChange={(e) => setBf({ ...bf, pp_bond_rate: e.target.value })} placeholder="P&P bond rate % (optional, at-award)"
                  className="text-[11px] border border-zinc-200 rounded-md px-2 py-1.5 sm:col-span-2" data-testid="bond-pprate-input" />
                <div className="sm:col-span-2 flex items-center gap-2">
                  <button onClick={saveBond} disabled={bondBusy} data-testid="bond-save-btn"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50">
                    {bondBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Record quote
                  </button>
                  <button onClick={() => setBondOpen(false)} className="text-[11px] text-zinc-500 hover:text-zinc-800" data-testid="bond-cancel-btn">Cancel</button>
                  <span className="text-[10px] text-zinc-400">Platform-only — never enters the CO package.</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Market comps (single magnitude-bounded source) — internal sanity band, never in the CO package */}
      {mc && (mc.n || 0) > 0 && (
        <div className="mt-3 border border-zinc-100 rounded-lg p-3" data-testid="market-comps-card">
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-zinc-500" />
            <span className="text-xs font-semibold text-zinc-800">Comparable federal awards</span>
          </div>
          <p className="text-[11px] text-zinc-600">
            Single magnitude-bounded source:
            <span className="font-medium"> n={mc.n}</span>, p25 <span className="font-medium">${Number(mc.p25 || 0).toLocaleString()}</span> · median <span className="font-medium">${Number(mc.median || 0).toLocaleString()}</span> · p75 <span className="font-medium">${Number(mc.p75 || 0).toLocaleString()}</span>
            {mc.within_band != null && (
              <span className={`ml-1 font-semibold ${mc.within_band ? "text-emerald-700" : "text-amber-700"}`}>
                {mc.within_band ? "· within band" : "· outside band"}
              </span>
            )}
          </p>
          {mc.provenance && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid="comps-provenance-chip">
              {mc.provenance.naics && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">NAICS {mc.provenance.naics}</span>}
              {mc.provenance.pop_state && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">PoP {mc.provenance.pop_state}</span>}
              {mc.provenance.psc && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">PSC {mc.provenance.psc}</span>}
              {mc.provenance.amount_window?.low != null && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">
                  ${Math.round(mc.provenance.amount_window.low).toLocaleString()}–${Math.round(mc.provenance.amount_window.high).toLocaleString()} window
                </span>
              )}
              {mc.provenance.years_back && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200">last {mc.provenance.years_back}y</span>}
              {mc.provenance.tier && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{mc.provenance.tier}</span>}
            </div>
          )}
          <ul className="mt-1 space-y-0.5">
            {(mc.records || []).map((rec, i) => (
              <li key={i} className="text-[10px] text-zinc-500 font-mono" data-testid={`comp-record-${i}`}>
                {rec.piid} · ${Number(rec.amount || 0).toLocaleString()} · {rec.recipient} · {rec.start_date} · {rec.agency}
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-zinc-400 mt-0.5">Live USASpending recompute — never a cached band. Internal sanity band only (never in the CO package).</p>
        </div>
      )}

      {/* Open estimate items */}
      {open.length > 0 && (
        <div className="mt-3" data-testid="pricing-readiness-open-items">
          <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1">Open pricing inputs ({open.length})</div>
          <ul className="space-y-1.5">
            {open.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-zinc-700" data-testid={`pricing-open-item-${i}`}>
                <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
